<template>
    <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
        <div class="a-header">
          <span class="a-header--title slide-down-enter-active">
            Activity Log
          </span>
            <div>
                <a-button size="default" class="a-header--button success-btn" @click="showDownloadPopup"> <a-icon type="cloud-download" /> Download </a-button>
            </div>
        </div>
        <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
            <Spinner />
        </div>
        <transition name="slide-up">
            <div v-if="!isLoading" class="a-filter">
                <a-row class="full-w" type="flex" justify="start" :gutter="24">
                    <a-col :span="8">
                        <div class="full-w a-input-group" :class="{ active: !!filterParams.search }">
                            <a-button size="large">
                                <a-icon type="search" />
                            </a-button>
                            <a-input class="custom-prefix" placeholder="Search" @change="onSearch">
                                <a-icon v-show="isSearching" slot="suffix" class="custom-suffix" type="loading" spin />
                            </a-input>
                        </div>
                    </a-col>
                    <a-col :span="8">
                        <div
                                class="full-w a-input-group"
                                :class="{active: !!filterParams.fromDate && !!filterParams.toDate,}"
                        >
                            <a-button size="large">
                                <a-icon type="filter" />
                            </a-button>
                            <a-range-picker :ranges="RANGE" :placeholder="['From', 'To']" :format="DATE_FORMAT.DDMMMYYYY" @change="onChangeDate" />
                        </div>
                    </a-col>
                </a-row>
                <a-row class="full-w" type="flex" justify="start" :gutter="24">
                    <a-col :span="24">
                        <div style="margin-top: 10px;" v-if="oldestEntry && latestEntry">Showing records from {{oldestEntry.timestamp + "+0000" | dateString(false) }} to {{latestEntry.timestamp + "+0000" | dateString(false) }}</div>
                    </a-col>
                </a-row>
            </div>
        </transition>
        <div v-resize="elSizeChange" class="a-body flex-grow-1 overflow-y-hidden">
            <transition name="slide-up">
                <template v-if="!isLoading">
                    <a-table
                            :row-key="record => record.id"
                            :columns="ActivityLogColumn"
                            :data-source="filterData"
                            :pagination="pagin"
                            :loading="isSearching"
                            :scroll="{x: tableSize.width - 12, y: tableSize.height - 29}"
                            :rowClassName="(record, index) => index % 2 === 0 ? 'table-row-dark' : 'table-row-light'"
                            @change="handleTableChange"
                    >
                        <template slot="timestamp" slot-scope="text, record">
                            <div>
                                <p class="mb-0">
                                    <time>{{ record.timestamp | dateString }}</time>
                                    &nbsp;
                                    <time>{{ record.timestamp | timeString }}</time>
                                </p>
                            </div>
                        </template>
                        <template slot="expandedRowRender" slot-scope="record" style="margin: 0">
                            <div>
                                <h3>Log Details</h3>
                                <p v-if="record.data.request.client_ip"><b>Client IP:</b> {{ record.data.request.client_ip }}</p>
                                <p>
                                    <div><b>Details:</b></div>
                                    <div v-for="message in record.data.messages" :key="message">
                                        {{ message }}
                                    </div>
                                </p>
                            </div>
                        </template>
                    </a-table>
                </template>
            </transition>
        </div>
        <div class="a-footer">
            <PaginationBar :pageSize.sync="pagin.pageSize" :total="pagin.total" @change="paginChange" />
        </div>

        <a-modal v-model="isShowDownloadPopup" title="Download Activity Log" :footer="null" :closable="true" :maskClosable="false" :keyboard="false">
            <a-form :form="downloadForm" @submit="handleDownloadSubmit">
                <a-row :gutter="24">
                    <a-col :span="24">
                        <a-form-item label="Date Range">
                            <a-range-picker
                                    :ranges="RANGE"
                                    :placeholder="['From', 'To']"
                                    :format="DATE_FORMAT.DDMMMYYYY"
                                    v-decorator="[
                                              'date_range',
                                              {
                                                  rules: [{ required: true, message: 'Please select a date range' },],
                                                  initialValue: RANGE['Last 3 Months']
                                              },
                                            ]"
                            />
                            <p v-if="allowedPeriodStartDate">Valid selection period: {{ allowedPeriodStartDate | dateString }} to {{ allowedPeriodEndDate | dateString }}</p>
                        </a-form-item>
                        <a-form-item label="Format">
                            <a-radio-group
                                    v-decorator="[
                                              'format',
                                              {
                                                  rules: [{ required: true, message: 'Please select a format' }],
                                                  initialValue: 'csv',
                                              },
                                            ]">
                                <a-radio value="csv">CSV</a-radio>
                                <a-radio value="json">JSON</a-radio>
                            </a-radio-group>
                        </a-form-item>
                    </a-col>
                </a-row>
                <a-row>
                    <a-col :span="24" :style="{ textAlign: 'right' }">
                        <a-button type="primary" html-type="submit" :loading="isDisableDownloadFile">
                            Download
                        </a-button>
                    </a-col>
                </a-row>
            </a-form>
        </a-modal>
    </div>
</template>

<script>
    import { debounce } from 'lodash';
    import { flatMapErrMessagesValidateForm, debounceTimeExecute } from '@shared/helpers/utils';
    import { PAGE_OPTIONS } from '@constants/pageOptions';
    import { DATE_FORMAT } from '@constants/dateFormat';
    import { API_URL } from '@shared/services/api.service';
    import { indexItemTable } from '@shared/helpers/utils';
    import { ActivityLogColumn } from '../columnsTable/ActivityLogColumn';
    import ActivityLogService from '../services/ActivityLog.service';
    import moment from "moment";
    import IncidentService from "../../Incident/services/Incident.service";

    export const RANGE = {
        'Today': [moment().set({ hour: 0, minute: 0, second: 0, millisecond: 1 }), moment().set({ hour: 23, minute: 59, second: 59, millisecond: 59 })],
        'Yesterday': [moment().subtract(1, 'days').set({ hour: 0, minute: 0, second: 0, millisecond: 1 }), moment().subtract(1, 'days').set({ hour: 23, minute: 59, second: 59, millisecond: 59 })],
        'This Week': [moment().set({ hour: 0, minute: 0, second: 0, millisecond: 1 }), moment().set({ hour: 23, minute: 59, second: 59, millisecond: 59 })],
        'Last Week': [moment().subtract(1, 'week').startOf('week'), moment().subtract(1, 'week').endOf('week')],
        'This Month': [moment().startOf('month'), moment().endOf('month')],
        'Last Month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
        'Last 3 Months': [moment().subtract(3, 'month').startOf('month'), moment().endOf('month')],
    };

    export default {
        name: 'ActivityLogList',
        data() {
            let pageSize = Number(window.localStorage.getItem("pageSize"));
            if(!pageSize) {
                pageSize = 5;
            }

            return {
                RANGE,
                DATE_FORMAT,
                PAGE_OPTIONS,
                ActivityLogColumn,
                filterData: [],
                filterParams: {
                    search: '',
                    fromDate: null,
                    toDate: null,
                },
                oldestEntry: null,
                latestEntry: null,
                allowedPeriodStartDate: null,
                allowedPeriodEndDate: null,
                pagin: {
                    current: 1,
                    pageSize: pageSize,
                    total: 0,
                },
                isSearching: false,
                isLoading: true,
                tableSize: {
                    height: '',
                    width: '',
                },
                isShowDownloadPopup: false,
                isDisableDownloadFile: false,
                downloadForm: this.$form.createForm(this, { name: 'download_form' }),
            };
        },
        mounted() {
            this.fetchList();
        },
        beforeDestroy() {},
        methods: {
            onSearch: debounce(function (inputChangeEvent) {
                const inputSearchValue = inputChangeEvent.target.value;
                this.filterParams.search = inputSearchValue ? String(inputSearchValue).trim().toLowerCase() : '';
                this.fetchList();
            }, 800),
            onChangeDate(dates, dateStrings) {
                console.log(dateStrings);
                this.filterParams.fromDate = dateStrings[0] ? moment(new Date(dateStrings[0]).setHours(0, 0, 0, 1)).format('YYYY-MM-DD HH:mm:ss') : null;
                this.filterParams.toDate = dateStrings[1] ? moment(new Date(dateStrings[1]).setHours(23, 59, 59, 59)).format('YYYY-MM-DD HH:mm:ss') : null;
                this.fetchList();
            },
            async fetchList() {
                try {
                    this.isSearching = true;
                    const responseData = await ActivityLogService.fetchList(this.pagin.current, this.pagin.pageSize, this.filterParams.search, this.filterParams.fromDate, this.filterParams.toDate);
                    const { data, total } = responseData;
                    this.filterData = (data || []);
                    this.oldestEntry = responseData.oldest_entry;
                    this.latestEntry = responseData.latest_entry;
                    this.pagin.total = total;
                    this.isSearching = false;
                } catch (err) {
                    //this.$showErrorMessage('HTTP error received from web server.!');
                    this.$showMultiErrorMessage(err.messages);
                } finally {
                    //if (this.isLoading) {
                        setTimeout(() => {
                            this.isLoading = false;
                            this.isSearching = false;
                        }, 300);
                    //}
                }
            },
            async showDownloadPopup() {
                this.isShowDownloadPopup = true;

                let allowedPeriod = await ActivityLogService.fetchAllowedPeriod();
                console.log(allowedPeriod);
                this.allowedPeriodStartDate = moment(allowedPeriod.start_date).toISOString();
                // Push the allowed period end date to the end of the month in the UI
                this.allowedPeriodEndDate = moment(allowedPeriod.end_date).endOf('month').toISOString();
            },
            paginChange(pagin) {
                this.pagin = { ...pagin };
                this.fetchList();
            },
            elSizeChange(size) {
                this.tableSize = size;
            },
            handleTableChange(pagination, filters, sorter) {
                console.log(pagination);
                const pager = { ...this.pagination };
                pager.current = pagination.current;
                this.pagin = pager;
                /*this.fetch({
                    results: pagination.pageSize,
                    page: pagination.current,
                    sortField: sorter.field,
                    sortOrder: sorter.order,
                    ...filters,
                });*/
                this.fetchList();
            },
            handleDownloadSubmit(e) {
                e.preventDefault();
                this.downloadForm.validateFields(async (err, values) => {
                    if (!err) {
                        console.log('Received values of form: ', values);

                        try {
                            this.isDisableDownloadFile = true;

                            const dateStrings = values.date_range;
                            const startDate = dateStrings[0] ? moment(new Date(dateStrings[0]).setHours(0, 0, 0, 1)).format('YYYY-MM-DD HH:mm:ss') : null;
                            const endDate = dateStrings[1] ? moment(new Date(dateStrings[1]).setHours(23, 59, 59, 59)).format('YYYY-MM-DD HH:mm:ss') : null;

                            const {url} = await ActivityLogService.fetchUrlOfFileToDownload(startDate, endDate, values.format);
                            //console.log(url);
                            const linkTag = document.createElement('a');
                            linkTag.href = `${API_URL}${url.replace(/api\//i, '')}`;
                            document.body.appendChild(linkTag);
                            linkTag.click();
                            document.body.removeChild(linkTag);
                        } catch (err) {
                            this.$showMultiErrorMessage(err.messages);
                        } finally {
                            setTimeout(() => {
                                this.isDisableDownloadFile = false;
                            }, 2000);
                        }
                    }
                });
            },
        },
    };
</script>

<style lang="scss" scoped>
    .time-text {
        margin-bottom: 0;
        white-space: nowrap;
    }
    .nav-link-detail {
        width: 100%;
        &:hover {
            text-decoration: underline;
        }
    }
    .nav-link-detail {
        display: inline-block;
    }
    .custom-suffix {
        font-size: 20px;
        color: #20a4b8;
    }
    .webpab-ext-link {
        color: inherit;
        display: block;
        width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        &:hover {
            color: #20a4b8;
            text-decoration: underline;
        }
    }
</style>

<style lang="scss">

    // Fix the spinner which somehow goes out of position
    .ant-spin-nested-loading.ant-table-spin-holder .ant-spin.ant-spin-spinning {
        position: absolute;
        left: 0px;
        top: 100px;
        margin-left: 50%;
    }

    table {
        width: 100%;
        margin: auto;
        table-layout: auto;
    }
    .fixed {
        table-layout: fixed;
    }

    th {
        background-color: #ffffff !important;
        padding: 10px;
        color: #323232;
        font-family: Roboto;
        font-size: 13px;
        font-weight: 500;
        letter-spacing: 0;
        line-height: 20px;
        &.sort > div {
            cursor: pointer;
            display: flex;
            align-items: flex-start;
        }
    }

    // Nth child doesn't work with the added expanded rows
    /*
    tbody > tr.ant-table-row-level-0 {
        &:not(:last-child) {
            border-bottom: 1px solid #e9e9e9;
        }
        &:nth-child(even) {
            background-color: #ffffff;
        }
        &:nth-child(odd) {
            background-color: #f7f7f7;
        }
        &:hover {
            background-color: #e9f7f7;
        }
    }
     */

    .table-row-light {
        background-color: #f9f9f9;
    }
    .table-row-dark {
        background-color: #f1f1f1;
    }

    .ant-table-expanded-row {
        background-color: #ffffff !important;
    }

    .ant-table-tbody > tr > td {
        border-bottom: 0 solid #e9e9e9 !important;
    }

    .ant-table-tbody > tr.ant-table-expanded-row > td {
        border-bottom: 1px solid #e9e9e9 !important;
    }
</style>
